// src/utils/annuaire.ts — SCE Issues
// Lecture de la recherche de personnes de SharePoint (le « sélecteur de
// personnes » du tenant, cf. api/annuaire.api). Pur, testable.
//
// Pourquoi : les FNC-T nomment leurs responsables en « Prénom Nom » ; les flows
// (F6) ont besoin de leur ADRESSE (colonnes LeaderUPN / ResponsableUPN). L'appli
// n'a que `User.Read` côté Graph (pas de recherche dans l'annuaire) ; SharePoint
// REST, lui, cherche dans tout le tenant avec le jeton qu'on a déjà.
//
// SharePoint renvoie une CHAÎNE JSON (tableau d'entités) :
//   { Key: 'i:0#.f|membership|mathieu.mourolin@sartorius.com',
//     DisplayText: 'Mourolin, Mathieu', EntityType: 'User',
//     EntityData: { Email, Department, Title (poste) } }

import { nomComplet } from './noms'
import { memePersonne } from './fnct'

export interface PersonneAnnuaire {
  /** « Prénom Nom », comme partout dans l'appli. */
  nom: string
  /** UPN (identifiant de connexion), en minuscules. */
  upn: string
  /** Adresse mail (souvent égale à l'UPN, pas toujours). */
  email: string
  service: string
  poste: string
  /** Compte invité (B2B) : pas un salarié. */
  invite: boolean
}

interface EntitePicker {
  Key?: string
  DisplayText?: string
  EntityType?: string
  EntityData?: { Email?: string; Department?: string; Title?: string }
}

const PREFIXE_COMPTE = /^i:0#\.f\|membership\|/i

/** Le résultat brut de `clientPeoplePickerSearchUser` → personnes, sans doublon. Jamais d'exception. */
export function lirePersonnesAnnuaire(brut: unknown): PersonneAnnuaire[] {
  let entites: unknown
  try {
    entites = typeof brut === 'string' ? JSON.parse(brut) : brut
  } catch {
    return []
  }
  if (!Array.isArray(entites)) return []
  const vus = new Set<string>()
  const res: PersonneAnnuaire[] = []
  for (const e of entites as EntitePicker[]) {
    if (e?.EntityType && e.EntityType !== 'User') continue
    const cle = e?.Key ?? ''
    if (!PREFIXE_COMPTE.test(cle)) continue
    const upn = cle.replace(PREFIXE_COMPTE, '').trim().toLowerCase()
    if (!upn || vus.has(upn)) continue
    vus.add(upn)
    res.push({
      nom: nomComplet(e.DisplayText ?? '', upn) || upn,
      upn,
      email: (e.EntityData?.Email ?? '').trim().toLowerCase(),
      service: (e.EntityData?.Department ?? '').trim(),
      poste: (e.EntityData?.Title ?? '').trim(),
      invite: upn.includes('#ext#'),
    })
  }
  return res
}

// ─── La recherche de personnes de SharePoint (02/11) ───
//
// Le sélecteur de personnes cherche dans tout Sartorius et n'en rend qu'une
// poignée : « Guillaume » sortait dix Guillaume d'ailleurs, et le seul de
// Pompey jamais. La recherche de personnes (`search/query`, source « People »)
// filtre sur le bureau dès la requête et rend le bureau de chaque résultat,
// sans lire les profils un par un.

/** La source de résultats « Personnes » de SharePoint (identifiant fixe, le même partout). */
export const SOURCE_PERSONNES = 'b09a7990-05ea-4af9-81ef-edfab16c4e31'

/** Les propriétés demandées à la recherche de personnes. */
export const PROPRIETES_RECHERCHE = ['AccountName', 'PreferredName', 'FirstName', 'LastName', 'WorkEmail', 'Department', 'JobTitle', 'OfficeNumber', 'BaseOfficeLocation', 'PictureURL']

/**
 * La requête KQL pour un début de nom : chaque mot devient un préfixe
 * (« guillaume del » → `guillaume* del*`), sans les signes qui casseraient la
 * requête. Vide si rien de cherchable.
 */
export function requeteNomKql(texte: string): string {
  return texte
    .split(/\s+/)
    .map((m) => m.replace(/[^\p{L}\p{N}'’.-]/gu, '').replace(/^[-.'’]+/, ''))
    .filter((m) => m.length > 0)
    .map((m) => `${m}*`)
    .join(' ')
}

/** La même requête, limitée aux personnes dont le bureau est Pompey (`NOVA_Pompey`). */
export function requeteNomPompeyKql(texte: string): string {
  const nom = requeteNomKql(texte)
  return nom ? `${nom} (OfficeNumber:NOVA_Pompey OR OfficeNumber:Pompey* OR BaseOfficeLocation:Pompey*)` : ''
}

type Cellule = { Key?: unknown; Value?: unknown }

const enTableau = <T>(v: unknown): T[] =>
  Array.isArray(v) ? (v as T[]) : Array.isArray((v as { results?: unknown })?.results) ? ((v as { results: T[] }).results) : []

/**
 * Le résultat brut de `search/query` (verbose ou non) → personnes, sans doublon
 * ni invité. `pompey` d'après le bureau ; null quand l'index ne le donne pas
 * (le profil tranchera). Jamais d'exception.
 */
export function lireRecherchePersonnes(brut: unknown): Personne[] {
  const racine = (brut as { query?: unknown })?.query ?? brut
  const table = (racine as { PrimaryQueryResult?: { RelevantResults?: { Table?: { Rows?: unknown } } } })?.PrimaryQueryResult?.RelevantResults?.Table
  const vus = new Set<string>()
  const res: Personne[] = []
  for (const ligne of enTableau<{ Cells?: unknown }>(table?.Rows)) {
    const v: Record<string, string> = {}
    for (const c of enTableau<Cellule>(ligne?.Cells)) {
      if (typeof c?.Key === 'string' && typeof c.Value === 'string') v[c.Key] = c.Value.trim()
    }
    const compte = v.AccountName ?? ''
    const upn = (PREFIXE_COMPTE.test(compte) ? compte.replace(PREFIXE_COMPTE, '') : compte.includes('@') ? compte : v.WorkEmail ?? '').trim().toLowerCase()
    if (!upn || !upn.includes('@') || upn.includes('#ext#') || vus.has(upn)) continue
    vus.add(upn)
    const bureau = `${v.OfficeNumber ?? ''} ${v.BaseOfficeLocation ?? ''}`.trim()
    const prenom = v.FirstName ?? ''
    const nom = v.LastName ?? ''
    res.push({
      nom: prenom && nom ? `${prenom} ${nom}` : nomComplet(v.PreferredName ?? '', upn) || upn,
      upn,
      email: (v.WorkEmail || upn).toLowerCase(),
      service: v.Department ?? '',
      poste: v.JobTitle ?? '',
      pompey: bureau ? /pompey/i.test(bureau) : null,
      photo: v.PictureURL || null,
    })
  }
  return res
}

// ─── Le profil d'une personne (07/10) ───
//
// La recherche ne rend ni le site ni la photo : on lit le profil de chaque
// résultat. Vu sur le vrai tenant (essai de /admin, 07/10) : `Office` =
// `NOVA_Pompey`, `FirstName` / `LastName` (plus sûrs que « Nom, Prénom » à
// découper : Jean-Philippe, particules), `Department` (service), `Title`
// (poste), `PictureUrl` (absente sans photo).

export interface ProprieteProfil {
  cle: string
  valeur: string
  /** Champ qui pourrait porter le site (bureau, lieu, ville, pays, société…). */
  lieu: boolean
  /** La valeur mentionne Pompey. */
  pompey: boolean
}

const CLES_LIEU = /office|location|city|site|country|company|street|state|postal|usage|bureau|ville/i

/**
 * Les propriétés NON VIDES d'un profil SharePoint (`PeopleManager/GetPropertiesFor`) :
 * celles de `UserProfileProperties` (`{ results: [{ Key, Value }] }` en verbose, ou
 * tableau direct) et les champs simples du profil (Title, Email…). Jamais d'exception.
 */
export function lireProprietesProfil(brut: unknown): ProprieteProfil[] {
  if (!brut || typeof brut !== 'object') return []
  const profil = brut as Record<string, unknown>
  const paires: [string, string][] = []
  for (const [cle, valeur] of Object.entries(profil)) {
    if (typeof valeur === 'string' && cle !== '__metadata') paires.push([cle, valeur])
  }
  const ups = profil.UserProfileProperties as { results?: unknown } | unknown[] | undefined
  const liste = Array.isArray(ups) ? ups : Array.isArray((ups as { results?: unknown })?.results) ? ((ups as { results: unknown[] }).results) : []
  for (const p of liste as { Key?: unknown; Value?: unknown }[]) {
    if (typeof p?.Key === 'string' && typeof p.Value === 'string') paires.push([p.Key, p.Value])
  }
  return paires
    .map(([cle, valeur]) => ({ cle, valeur: valeur.trim() }))
    .filter((p) => p.valeur !== '')
    .map((p) => ({ ...p, lieu: CLES_LIEU.test(p.cle), pompey: /pompey/i.test(p.valeur) }))
}

/** Une personne de l'annuaire, complétée par son profil. */
export interface Personne {
  /** « Prénom Nom ». */
  nom: string
  /** UPN, en minuscules : ce qu'on écrit (LeaderUPN / ResponsableUPN). */
  upn: string
  email: string
  service: string
  poste: string
  /** Rattachée à Pompey ; null : profil illisible (on ne la cache pas). */
  pompey: boolean | null
  /** Adresse SharePoint du fichier photo (`sartorius-my`…) ; null : pas de photo. */
  photo: string | null
  /** Compte invité (B2B, externe) : seulement quand la recherche les demande. */
  invite?: boolean
}

const valeurDe = (props: readonly ProprieteProfil[], ...cles: string[]): string => {
  for (const c of cles) {
    const v = props.find((x) => x.cle.toLowerCase() === c.toLowerCase())?.valeur
    if (v) return v
  }
  return ''
}

/** Le résultat de la recherche, complété par le profil (null = profil illisible : on garde la recherche). */
export function completerPersonne(p: PersonneAnnuaire, props: readonly ProprieteProfil[] | null): Personne {
  const invite = p.invite ? { invite: true } : {}
  if (!props) return { nom: p.nom, upn: p.upn, email: p.email, service: p.service, poste: p.poste, pompey: null, photo: null, ...invite }
  const prenom = valeurDe(props, 'FirstName')
  const nom = valeurDe(props, 'LastName')
  return {
    nom: prenom && nom ? `${prenom} ${nom}` : p.nom,
    upn: p.upn,
    email: (valeurDe(props, 'WorkEmail', 'Email') || p.email).toLowerCase(),
    service: valeurDe(props, 'Department') || p.service,
    poste: valeurDe(props, 'Title') || p.poste,
    pompey: /pompey/i.test(valeurDe(props, 'Office')),
    photo: valeurDe(props, 'PictureUrl', 'PictureURL') || null,
    ...invite,
  }
}

/** Une personne connue par son seul UPN (étiquette d'une personne déjà choisie). */
export function personneDepuisProfil(upn: string, nomConnu: string, props: readonly ProprieteProfil[] | null): Personne {
  const base: PersonneAnnuaire = { nom: nomConnu, upn: upn.toLowerCase(), email: '', service: '', poste: '', invite: false }
  return completerPersonne(base, props)
}

/**
 * Pompey d'abord (Guillaume, 07/10) : Pompey (et les profils illisibles, pour
 * ne cacher personne), puis les autres sites — montrés d'office depuis le
 * 22/10 (plus de « Chercher hors Pompey ») —, puis les externes (invités B2B),
 * quand la recherche les a demandés.
 */
export function partagerParSite(personnes: readonly Personne[]): { pompey: Personne[]; autres: Personne[]; externes: Personne[] } {
  const internes = personnes.filter((p) => !p.invite)
  return {
    pompey: internes.filter((p) => p.pompey !== false),
    autres: internes.filter((p) => p.pompey === false),
    externes: personnes.filter((p) => p.invite),
  }
}

/** Le domaine de l'adresse d'un externe (« vinci-energies.net ») : ce qui le situe dans une liste. */
export function domaineExterne(p: Pick<Personne, 'email' | 'upn'>): string {
  const adresse = p.email || p.upn
  return adresse.includes('@') && !adresse.includes('#ext#') ? (adresse.split('@')[1] ?? '') : ''
}

/**
 * Retrouver une personne d'après son « Prénom Nom » (nom pré-rempli, habitué,
 * nom repris de l'Excel) : la SEULE de ce nom, de Pompey si plusieurs ; sinon
 * null — jamais de devinette.
 */
export function retrouverParNom(nom: string, personnes: readonly Personne[]): Personne | null {
  return retrouverParNomDetail(nom, personnes).personne
}

/** Pourquoi une personne n'est pas retrouvée : absente, ou plusieurs du même nom (homonymes, comptes en double). */
export type RaisonNom = 'trouve' | 'absent' | 'plusieurs'

/** Comme retrouverParNom, en disant pourquoi rien n'est retenu (l'étiquette l'affiche). */
export function retrouverParNomDetail(nom: string, personnes: readonly Personne[]): { personne: Personne | null; raison: RaisonNom } {
  const memes = personnes.filter((p, i) => memePersonne(p.nom, nom) && personnes.findIndex((x) => x.upn === p.upn) === i)
  if (memes.length === 0) return { personne: null, raison: 'absent' }
  if (memes.length === 1) return { personne: memes[0] ?? null, raison: 'trouve' }
  const aPompey = memes.filter((p) => p.pompey === true)
  return aPompey.length === 1 ? { personne: aPompey[0] ?? null, raison: 'trouve' } : { personne: null, raison: 'plusieurs' }
}

/** Le nom de famille d'un « Prénom Nom » (dernier mot), pour une seconde recherche dans l'annuaire. */
export function nomDeFamille(nom: string): string {
  const mots = nom.trim().split(/\s+/)
  return mots.length > 1 ? (mots.at(-1) ?? '') : ''
}

/**
 * Les noms les plus fréquents (habitués d'un champ vide), du plus au moins
 * fréquent. Les graphies d'une même personne (« Vautelin  Sébastien ») sont
 * réunies ; la plus fréquente est montrée.
 */
export function lesPlusFrequents(noms: readonly string[], combien = 6): string[] {
  const personnes: { graphies: Map<string, number>; n: number }[] = []
  for (const brut of noms) {
    const nom = brut.trim()
    if (!nom) continue
    let p = personnes.find((x) => memePersonne([...x.graphies.keys()][0], nom))
    if (!p) {
      p = { graphies: new Map(), n: 0 }
      personnes.push(p)
    }
    p.n += 1
    p.graphies.set(nom, (p.graphies.get(nom) ?? 0) + 1)
  }
  const montree = (p: { graphies: Map<string, number> }): string =>
    [...p.graphies.entries()].reduce((a, b) => (b[1] > a[1] ? b : a))[0]
  return personnes
    .map((p) => ({ nom: montree(p), n: p.n }))
    .sort((a, b) => b.n - a.n || a.nom.localeCompare(b.nom, 'fr'))
    .slice(0, combien)
    .map((p) => p.nom)
}
